import axios from "axios";
import {
	parseJsonWithBigInt,
	removeNullParamsFromObject
} from "@/helpers/functions.helper.js";
import MapboxService from "@/services/v1/Mapbox.service.js";
import { $dayjs } from "@/plugins/dayjs/dayjs.plugin.js";
import { $log } from "@/plugins/logger/logger.plugin.js";
import requestAll from "../requestAll.js";

const API = import.meta.env.VITE_API;

// function that will update device tags adding the box address. This way, it won't be needed to request mapbox everytime to convert coordinates to address
const addAddressesToDevices = async (devices) => {
	await Promise.all(
		devices.map(async (device) => {
			if (
				device?.tags?.ws_box_geolocation &&
				!device?.tags?.ws_box_address
			) {
				const boxCoordinates = device.tags.ws_box_geolocation
					.split(";")
					.reverse();
				try {
					const address = await MapboxService.getLocation({
						term: boxCoordinates
					});
					if (address.features.length > 0) {
						await DeviceService.updateDevice(
							{
								tags: {
									...device.tags,
									ws_box_address:
										address.features[0].place_name
								}
							},
							device.deviceId
						);
					} else {
						$log.error("coordinates dont have address");
					}
				} catch (error) {
					$log.error("failed getLocation from mapbox", error);
				}
			}
		})
	);
};

/**
 * Due to int64 data format for some properties, javascript can't parse them as a Number so it's needed a BigInt handler - json-bigint does that (using json.js and bignumber.js libraries)
 * @param {string} data
 * @returns {any}
 */
function transformResponseWithBigInt(data) {
	if (!data) {
		return [];
	}
	const dataJson = JSON.parse(data);
	for (const value of dataJson) {
		const payload = value.scaledPayload || value.payload;
		try {
			const payloadNumber = Number(payload);
			if (
				!Number.isNaN(payloadNumber) &&
				!Number.isSafeInteger(payloadNumber)
			) {
				return parseJsonWithBigInt(data);
			}
		} catch {
			return dataJson;
		}
	}
	return dataJson;
}

const DeviceService = {
	deviceId: null,
	listDevices: {},

	setDeviceId(deviceId) {
		this.deviceId = deviceId;
	},

	/**
	 * @param {object} params
	 * @returns {Promise} Array with activated devices
	 */
	getDevices(params = {}) {
		return requestAll(`${API}/devices`, params)
			.then((devices) => {
				if (params?.commercialType?.search("HUB") === -1) {
					addAddressesToDevices(devices);
				}
				return devices;
			})
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Get only Tower Boxes
	 *
	 * @param {object} params Pass organizationId to get activated boxes from a single organization
	 * @returns {Promise} Array with activated boxes
	 */
	getOnlyBoxes(params) {
		return this.getDevices({ ...params, deviceType: "TOWER" });
	},

	/**
	 * Activate a Device for connect to platform
	 * @param {*} params
	 * @returns
	 */
	activateDevice(params) {
		return axios
			.post(`${API}/devices/activate`, params)
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Get a device by its ID
	 * @param {string | null} deviceId - The ID of the device to get
	 * @param {boolean} useCache - Whether to use cached data
	 */
	getDevice(deviceId = null, useCache = true, params = {}) {
		const _deviceId = deviceId || this.deviceId;

		if (useCache && this.listDevices[_deviceId]) {
			return Promise.resolve(this.listDevices[_deviceId]);
		}

		return axios
			.get(`${API}/devices/${_deviceId}`, { params })
			.then((res) => {
				this.listDevices[_deviceId] = res.data;
				return res.data;
			})
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Get all the data to feed the graph
	 *
	 * This uses the new /grouped api, the data is optimized in size, where the response looks like:
	 * ```json
	 * [
	 *   { "propertyId": "first", "values": [{"t": <timestamp>, "p": <payload>, "s": <scaledPayload> }, ...]},
	 *   { "propertyId": "second", "values": [{"t": <timestamp>, "p": <payload>, "s": <scaledPayload> }, ...]},
	 *   ...
	 * ]
	 * ```
	 */
	async fetchAllGraphValues(deviceId, properties, params, aggregation) {
		const data = [];
		let res;
		const size = params.size || 10000;

		for (let page = params.page || 1; ; page++) {
			try {
				const suffix =
					aggregation && aggregation.aggregationMethod !== "NONE"
						? `analytics`
						: `grouped?size=${size}&page=${page}`;
				// eslint-disable-next-line no-await-in-loop
				res = await axios.post(
					`${API}/devices/${deviceId}/properties/${suffix}`,
					{
						properties: properties,
						since: params.since,
						until: params.until,
						aggregationMethod: aggregation?.aggregationMethod,
						bin: aggregation?.bin,
						tz: aggregation?.tz
					},
					{ transformResponse: [parseJsonWithBigInt] }
				);
			} catch (error) {
				$log.debug("Failed getting data", error);
				throw error;
			}

			if (!res.data || res.status !== 200) {
				break;
			}

			let received = 0;
			res.data.forEach((entry) => {
				const values = entry.values.map((value) => {
					return {
						deviceId: deviceId,
						property: entry.propertyId,
						timestamp: value.t,
						payload: value.p,
						scaledPayload: value.s,
						name: entry.name,
						externalId: entry.externalId
					};
				});
				data.push(...values);
				received += values.length;
			});

			if (received < size || aggregation?.aggregationMethod !== "NONE") {
				break;
			}
		}

		return data;
	},

	async getAllProperties(propertyIds, params, deviceId = null, aggregation) {
		const fourteenDaysAgo = $dayjs().subtract(14, "days").valueOf();
		const now = $dayjs().valueOf();

		return this.fetchAllGraphValues(
			deviceId || this.deviceId,
			propertyIds,
			{
				since: fourteenDaysAgo,
				until: now,
				sort: "timestamp,desc",
				...params
			},
			aggregation
		)
			.then((data) => data)
			.catch((err) => {
				throw err.response;
			});
	},

	getProperties(
		property = null,
		size = 20,
		page = 1,
		deviceId = null,
		timestampSince = null,
		timestampUntil = null
	) {
		const fourteenDaysAgo = $dayjs().subtract(14, "days").valueOf();
		const now = $dayjs().valueOf();

		const params = removeNullParamsFromObject({
			property: property,
			size: size,
			page: page,
			since: timestampSince || fourteenDaysAgo,
			until: timestampUntil || now
		});
		return axios
			.get(`${API}/devices/${deviceId || this.deviceId}/properties`, {
				params,
				transformResponse: [
					function (data) {
						return transformResponseWithBigInt(data);
					}
				]
			})
			.then((res) => res.data)
			.catch((err) => {
				throw err.response;
			});
	},

	setProperty({
		propertyId = null,
		payload = null,
		payloadType = null,
		deviceId = null,
		isScaled = null
	}) {
		if (!propertyId) {
			throw new Error("You must provide a property Id or property slug");
		}

		let setOutOfService = null;
		if (
			payload &&
			Object.prototype.hasOwnProperty.call(payload, "setOutOfService")
		) {
			setOutOfService = payload.setOutOfService;
			payload.setOutOfService = null;
		}

		let final = {
			payload: payload,
			nullify: payload === null,
			setOutOfService
		};

		if (payloadType) {
			final.payloadType = payloadType;
		}
		if (isScaled !== null) {
			final.isScaled = isScaled;
		}
		return axios
			.put(
				`${API}/devices/${
					deviceId || this.deviceId
				}/properties/${propertyId}`,
				final
			)
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getBacnetAutoDiscovery(networkId = null, deviceId = null) {
		if (networkId === null) {
			throw new Error("You must provide Network ID");
		}

		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/configs/current/networks/${networkId}/discovery`
			)
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	requestAutoDiscovery(networkId = null, deviceId = null) {
		if (networkId === null) {
			throw new Error("You must provide Network ID");
		}

		return this.sendCommand("DISCOVER", [networkId], deviceId);
	},

	sendCommand(command, commandArguments = [], deviceId = null) {
		return axios
			.post(`${API}/devices/${deviceId || this.deviceId}/commands`, {
				command: command,
				arguments: commandArguments
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getCommand(commandId = null, deviceId = null, params = {}) {
		if (commandId === null) {
			throw new Error("You must provide a Command ID");
		}

		return axios
			.get(
				`${API}/devices/${
					deviceId || this.deviceId
				}/commands/${commandId}`,
				{ params }
			)
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getListCommands(paramsOrDeviceId = null, _deviceId = null) {
		const deviceId =
			paramsOrDeviceId && typeof paramsOrDeviceId === "string"
				? paramsOrDeviceId
				: _deviceId;
		const params =
			paramsOrDeviceId &&
			!Array.isArray(paramsOrDeviceId) &&
			typeof paramsOrDeviceId === "object"
				? paramsOrDeviceId
				: {};
		return axios
			.get(`${API}/devices/${deviceId || this.deviceId}/commands`, {
				params
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	getEvents(deviceId = null, params = {}) {
		return axios
			.get(`${API}/devices/${deviceId || this.deviceId}/events`, {
				params
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Update device info
	 *
	 * @param {*} params
	 * @param {string| null} [deviceId=null]
	 * @returns {*}
	 */
	updateDevice(params, deviceId = null) {
		return axios
			.put(`${API}/devices/${deviceId || this.deviceId}`, params)
			.then((resp) => resp.data)
			.then((data) => {
				this.listDevices[deviceId || this.deviceId] = data;
				return data;
			})
			.catch((err) => {
				throw err.response;
			});
	},

	/**
	 * Update device auto-update feature
	 *
	 * @param {string} deviceId
	 * @param {boolean} autoUpdate
	 * @returns {*}
	 */
	updateDeviceAutoUpdate(deviceId, autoUpdate) {
		const route = autoUpdate ? "enable" : "disable";
		return axios
			.put(
				`${API}/devices/${deviceId}/features/ENABLE_AUTOMATIC_UPDATES/${route}`
			)
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	},

	checkHardwareCrc(hardwareId, crc) {
		return axios
			.post(`${API}/devices/checkHardwareCrc`, {
				hardwareId,
				crc
			})
			.then((resp) => resp.data)
			.catch((err) => {
				throw err.response;
			});
	}
};

export default DeviceService;
